import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Card, Row, Col, Badge, Button, Form, Table, ProgressBar, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";

const FILTER_STORAGE_KEY = "procurement_pipeline_filters";

const getSavedFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return {};
};

const ProcurementExecutionDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters (Restored from sessionStorage if previously filtered)
  const savedFilters = getSavedFilters();
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedProject, setSelectedProject] = useState(savedFilters.selectedProject || "");
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(savedFilters.selectedWorkOrder || "");
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter || "ALL");
  const [search, setSearch] = useState(savedFilters.search || "");
  const [page, setPage] = useState(Number(savedFilters.page) || 1);
  const [limit, setLimit] = useState(Number(savedFilters.limit) || 10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, limit: Number(savedFilters.limit) || 10 });

  // Persist active filters across navigation (preview -> back)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          selectedProject,
          selectedWorkOrder,
          statusFilter,
          search,
          page,
          limit,
        })
      );
    } catch (_) {}
  }, [selectedProject, selectedWorkOrder, statusFilter, search, page, limit]);


  // Load Projects for Dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects");
        setProjects(res.data?.data || res.data || []);
      } catch (err) {
        console.error("Error loading projects:", err);
      }
    };
    fetchProjects();
  }, []);

  // Load Work Orders
  useEffect(() => {
    const fetchWorkOrders = async () => {
      try {
        const params = { limit: 100 };
        if (selectedProject) params.project = selectedProject;
        const res = await api.get("/work-orders", { params });
        setWorkOrders(res.data?.data || res.data || []);
      } catch (err) {
        console.error("Error loading work orders:", err);
      }
    };
    fetchWorkOrders();
  }, [selectedProject]);

  // Load KPI Stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      let query = `/pipeline-tracking/dashboard?`;
      if (selectedProject) query += `projectId=${selectedProject}&`;
      if (selectedWorkOrder) query += `workOrderId=${selectedWorkOrder}&`;
      const res = await api.get(query);
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error("Error loading pipeline stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedProject, selectedWorkOrder]);

  // Load Pipeline Chains
  const loadChains = useCallback(async () => {
    setLoading(true);
    try {
      let query = `/pipeline-tracking/chains?page=${page}&limit=${limit}&`;
      if (selectedProject) query += `projectId=${selectedProject}&`;
      if (selectedWorkOrder) query += `workOrderId=${selectedWorkOrder}&`;
      if (statusFilter && statusFilter !== "ALL") query += `status=${statusFilter}&`;
      if (search.trim()) query += `search=${encodeURIComponent(search.trim())}&`;

      const res = await api.get(query);
      if (res.data?.success) {
        setChains(res.data.data.chains || []);
        setPagination(res.data.data.pagination || { total: 0, totalPages: 1, limit: limit });
      }
    } catch (err) {
      console.error("Error loading pipeline chains:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedWorkOrder, statusFilter, search, page, limit]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadChains();
  }, [loadChains]);

  // Consolidated PO Items Helper (aggregates multiple POs per unique Indent item)
  const getConsolidatedPoItems = (chain) => {
    if (!chain) return [];
    const indentItems = chain.indent?.items || [];
    const pos = chain.pos || [];

    const itemsMap = {};

    // 1. Seed with indent items
    indentItems.forEach((indItm) => {
      const rawName = (indItm.itemName || indItm.materialName || "").trim();
      const cleanKey = rawName.toLowerCase();
      if (!cleanKey) return;

      itemsMap[cleanKey] = {
        itemName: rawName,
        raisedQty: Number(indItm.quantityRequested) || 0,
        totalOrderedQty: 0,
        balanceQty: Number(indItm.quantityRequested) || 0,
        unit: indItm.unit || "Nos",
        subItems: indItm.subItems || [],
        subItemsOrdered: {},
        linkedPos: [],
        totalAmount: 0,
      };
    });

    // 2. Aggregate from linked POs
    pos.forEach((po) => {
      (po.items || []).forEach((pItm) => {
        const rawName = (pItm.itemName || pItm.name || "").trim();
        const pName = rawName.toLowerCase();
        if (!pName) return;

        let matchedKey = Object.keys(itemsMap).find((k) => {
          if (k === pName) return true;
          const cleanK = k.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          const cleanP = pName.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          return cleanK === cleanP || (cleanK.length > 5 && cleanP.length > 5 && (cleanK === cleanP || cleanK.includes(cleanP) || cleanP.includes(cleanK)));
        });

        // Skip items that were not requested in this indent
        if (!matchedKey) return;

        const qty = Number(pItm.quantity) || 0;
        const rate = Number(pItm.rate) || 0;
        const amt = Number(pItm.amount || (qty * rate)) || 0;

        itemsMap[matchedKey].totalOrderedQty += qty;
        itemsMap[matchedKey].totalAmount += amt;

        itemsMap[matchedKey].linkedPos.push({
          poId: po._id,
          poNumber: po.poNumber,
          status: po.status,
          vendorName: po.vendorName,
          createdAt: po.createdAt,
          quantity: qty,
          unit: pItm.unit || itemsMap[matchedKey].unit,
          rate: rate,
          amount: amt,
        });

        (pItm.subItems || []).forEach((s) => {
          const sKey = (s.name || "").trim().toLowerCase();
          if (sKey) {
            itemsMap[matchedKey].subItemsOrdered[sKey] =
              (itemsMap[matchedKey].subItemsOrdered[sKey] || 0) + (Number(s.quantity) || 0);
          }
        });
      });
    });

    return Object.values(itemsMap).map((itm) => ({
      ...itm,
      balanceQty: Math.max(0, itm.raisedQty - itm.totalOrderedQty),
    }));
  };

  // Consolidated Delivery Items Helper (tracks Raised -> PO -> Delivered per unique Indent item)
  const getConsolidatedDeliveryItems = (chain) => {
    if (!chain) return [];
    const indentItems = chain.indent?.items || [];
    const pos = chain.pos || [];
    const deliveries = chain.deliveries || [];

    const itemsMap = {};

    // 1. Seed with indent items
    indentItems.forEach((indItm) => {
      const rawName = (indItm.itemName || indItm.materialName || "").trim();
      const cleanKey = rawName.toLowerCase();
      if (!cleanKey) return;

      itemsMap[cleanKey] = {
        itemName: rawName,
        raisedQty: Number(indItm.quantityRequested) || 0,
        totalOrderedQty: 0,
        totalDeliveredQty: 0,
        unit: indItm.unit || "Nos",
        subItems: indItm.subItems || [],
        subItemsOrdered: {},
        subItemsDelivered: {},
        linkedDeliveries: [],
      };
    });

    // 2. Aggregate PO Ordered Qty
    pos.forEach((po) => {
      (po.items || []).forEach((pItm) => {
        const rawName = (pItm.itemName || pItm.name || "").trim();
        const pName = rawName.toLowerCase();
        if (!pName) return;

        let matchedKey = Object.keys(itemsMap).find((k) => {
          if (k === pName) return true;
          const cleanK = k.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          const cleanP = pName.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          return cleanK === cleanP || (cleanK.length > 5 && cleanP.length > 5 && (cleanK === cleanP || cleanK.includes(cleanP) || cleanP.includes(cleanK)));
        });

        // Skip items that were not requested in this indent
        if (!matchedKey) return;

        const qty = Number(pItm.quantity) || 0;
        itemsMap[matchedKey].totalOrderedQty += qty;

        (pItm.subItems || []).forEach((s) => {
          const sKey = (s.name || "").trim().toLowerCase();
          if (sKey) {
            itemsMap[matchedKey].subItemsOrdered[sKey] =
              (itemsMap[matchedKey].subItemsOrdered[sKey] || 0) + (Number(s.quantity) || 0);
          }
        });
      });
    });

    // 3. Aggregate Deliveries
    deliveries.forEach((del) => {
      (del.items || []).forEach((dItm) => {
        const rawName = (dItm.itemName || dItm.name || "").trim();
        const dName = rawName.toLowerCase();
        if (!dName) return;

        let matchedKey = Object.keys(itemsMap).find((k) => {
          if (k === dName) return true;
          const cleanK = k.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          const cleanD = dName.replace(/^\[wli-[^\]]+\]\s*/i, "").trim();
          return cleanK === cleanD || (cleanK.length > 5 && cleanD.length > 5 && (cleanK === cleanD || cleanK.includes(cleanD) || cleanD.includes(cleanK)));
        });

        // Skip items that were not requested in this indent
        if (!matchedKey) return;

        const delQty = Number(dItm.quantityDelivered ?? dItm.receivedQuantity ?? dItm.challanQuantity ?? dItm.quantity ?? 0);
        itemsMap[matchedKey].totalDeliveredQty += delQty;

        itemsMap[matchedKey].linkedDeliveries.push({
          deliveryId: del._id,
          deliveryNumber: del.deliveryNumber,
          challanNumber: del.challanNumber,
          status: del.status || "Delivered",
          vendorName: del.vendorName,
          createdAt: del.createdAt,
          quantity: delQty,
          unit: dItm.unit || itemsMap[matchedKey].unit,
        });

        (dItm.subItems || []).forEach((s) => {
          const sKey = (s.name || "").trim().toLowerCase();
          if (sKey) {
            itemsMap[matchedKey].subItemsDelivered[sKey] =
              (itemsMap[matchedKey].subItemsDelivered[sKey] || 0) + (Number(s.quantity) || 0);
          }
        });
      });
    });

    return Object.values(itemsMap).map((itm) => {
      const targetQty = itm.totalOrderedQty > 0 ? itm.totalOrderedQty : itm.raisedQty;
      return {
        ...itm,
        pendingDeliveryQty: Math.max(0, targetQty - itm.totalDeliveredQty),
      };
    });
  };

  const handleOpenDetail = (chain) => {
    const targetId = chain._id || chain.indent?._id;
    navigate(`/pipeline-detail?id=${targetId}`, { state: { chain } });
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime())
      ? "—"
      : date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getStageBadge = (stage) => {
    const baseStyle = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      fontWeight: 700,
      borderRadius: "50rem",
      fontSize: "11px",
      padding: "3px 10px",
    };
    switch (stage) {
      case "INDENT_PENDING":
        return <span style={{ ...baseStyle, backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>Indent Pending</span>;
      case "INDENT_APPROVED":
        return <span style={{ ...baseStyle, backgroundColor: "#ede9fe", color: "#5b21b6", border: "1px solid #ddd6fe" }}>Indent Approved</span>;
      case "PO_ISSUED":
        return <span style={{ ...baseStyle, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" }}>PO Issued</span>;
      case "DELIVERED":
        return <span style={{ ...baseStyle, backgroundColor: "#ffedd5", color: "#9a3412", border: "1px solid #fdba74" }}>Delivered</span>;
      case "IN_EXECUTION":
        return <span style={{ ...baseStyle, backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc" }}>In DPR</span>;
      case "COMPLETED":
        return <span style={{ ...baseStyle, backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" }}>Completed</span>;
      default:
        return <span style={{ ...baseStyle, backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>Pending</span>;
    }
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu="PO, Indent & DPR Tracking"
        motherMenu="Dashboard"
        pageContent="Procurement & Execution Pipeline"
      />

      {/* ========================================================
          1. STATS WIDGET CARDS (Standard Canscorp Theme)
      ======================================================== */}
      <Row className="mb-3">
        {/* Card 1: Indents */}
        <Col xl={3} sm={6} className="mb-3">
          <Card className="shadow-sm h-100 mb-0">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 font-w600 text-uppercase">1. Site Indents</span>
                  <h3 className="font-w700 mb-0 mt-1">
                    {statsLoading ? <Spinner size="sm" animation="border" /> : stats?.kpis?.indents?.total || 0}
                  </h3>
                  <span className="fs-12 text-muted">
                    <span className="font-w600" style={{ color: "#166534" }}>{stats?.kpis?.indents?.approved || 0} Approved</span> •{" "}
                    <span className="font-w600" style={{ color: "#b45309" }}>{stats?.kpis?.indents?.pending || 0} Pending</span>
                  </span>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "48px", height: "48px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706" }}
                >
                  <i className="fas fa-clipboard-list" style={{ fontSize: "20px" }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Card 2: Purchase Orders */}
        <Col xl={3} sm={6} className="mb-3">
          <Card className="shadow-sm h-100 mb-0">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 font-w600 text-uppercase">2. Purchase Orders</span>
                  <h3 className="font-w700 mb-0 mt-1">
                    {statsLoading ? <Spinner size="sm" animation="border" /> : stats?.kpis?.purchaseOrders?.total || 0}
                  </h3>
                  <span className="fs-12 text-muted">
                    Value: <strong style={{ color: "#1d4ed8" }}>₹{Math.round(stats?.kpis?.purchaseOrders?.totalValue || 0).toLocaleString("en-IN")}</strong>
                  </span>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "48px", height: "48px", background: "rgba(37, 99, 235, 0.15)", color: "#2563eb" }}
                >
                  <i className="fas fa-shopping-cart" style={{ fontSize: "20px" }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Card 3: Deliveries */}
        <Col xl={3} sm={6} className="mb-3">
          <Card className="shadow-sm h-100 mb-0">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 font-w600 text-uppercase">3. Site Deliveries (MIR)</span>
                  <h3 className="font-w700 mb-0 mt-1">
                    {statsLoading ? <Spinner size="sm" animation="border" /> : stats?.kpis?.deliveries?.total || 0}
                  </h3>
                  <span className="fs-12 text-muted">
                    Supply Rate: <strong style={{ color: "#0891b2" }}>{stats?.pipelineHealth?.poToDeliveryRate || 0}%</strong>
                  </span>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "48px", height: "48px", background: "rgba(8, 145, 178, 0.15)", color: "#0891b2" }}
                >
                  <i className="fas fa-truck" style={{ fontSize: "20px" }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Card 4: DPR Site Execution */}
        <Col xl={3} sm={6} className="mb-3">
          <Card className="shadow-sm h-100 mb-0">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 font-w600 text-uppercase">4. DPR Site Execution</span>
                  <h3 className="font-w700 mb-0 mt-1">
                    {statsLoading ? <Spinner size="sm" animation="border" /> : stats?.kpis?.dprExecution?.totalDPRs || 0}
                  </h3>
                  <span className="fs-12 text-muted">
                    Activities: <strong style={{ color: "#15803d" }}>{stats?.kpis?.dprExecution?.totalActivities || 0} Logged</strong>
                  </span>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "48px", height: "48px", background: "rgba(16, 185, 129, 0.15)", color: "#059669" }}
                >
                  <i className="fas fa-hard-hat" style={{ fontSize: "20px" }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========================================================
          2. FILTER BAR (Standard Canscorp Form Controls)
      ======================================================== */}
      <Row className="mb-3">
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Body className="py-3 px-3">
              <div className="d-flex flex-wrap gap-2 align-items-end">
                {/* Search */}
                <div style={{ flex: "1.2 1 150px", minWidth: "140px" }}>
                  <label className="form-label font-w600 mb-1 small text-nowrap">Search Requisition / Item</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ height: "36px" }}
                    placeholder="Search Indent No, Item..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && setPage(1)}
                  />
                </div>

                {/* Project Filter */}
                <div style={{ flex: "1.4 1 180px", minWidth: "160px" }}>
                  <label className="form-label font-w600 mb-1 small text-nowrap">Project</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ height: "36px" }}
                    value={selectedProject}
                    onChange={(e) => {
                      setSelectedProject(e.target.value);
                      setSelectedWorkOrder("");
                      setPage(1);
                    }}
                  >
                    <option value="">All Projects</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.projectName || p.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Work Order Filter */}
                <div style={{ flex: "1.4 1 180px", minWidth: "160px" }}>
                  <label className="form-label font-w600 mb-1 small text-nowrap">Work Order</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ height: "36px" }}
                    value={selectedWorkOrder}
                    onChange={(e) => {
                      const woId = e.target.value;
                      setSelectedWorkOrder(woId);
                      if (woId && !selectedProject) {
                        const found = workOrders.find((w) => w._id === woId);
                        if (found?.project?._id || found?.project) {
                          setSelectedProject(found.project._id || found.project);
                        }
                      }
                      setPage(1);
                    }}
                  >
                    <option value="">All Work Orders</option>
                    {workOrders.map((w) => {
                      const woNumber = w.woNo || w.woNumber || "";
                      const woLabel = w.label
                        ? `${w.label}${woNumber ? ` (${woNumber})` : ""}`
                        : (woNumber || "Work Order");
                      const projName = !selectedProject
                        ? (w.project?.projectName || w.projectName || w.project?.name)
                        : null;
                      return (
                        <option key={w._id} value={w._id}>
                          {woLabel}{projName ? ` – ${projName}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Status Filter */}
                <div style={{ flex: "1 1 140px", minWidth: "130px" }}>
                  <label className="form-label font-w600 mb-1 small text-nowrap">Stage Filter</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ height: "36px" }}
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="ALL">All Stages</option>
                    <option value="INDENT_PENDING">Indent Pending</option>
                    <option value="INDENT_APPROVED">Indent Approved</option>
                    <option value="PO_ISSUED">PO Issued</option>
                    <option value="DELIVERED">Delivered on Site</option>
                    <option value="IN_EXECUTION">In DPR Execution</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>

                {/* Reset & Refresh Buttons */}
                <div className="d-flex gap-2 align-items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    style={{ height: "36px", padding: "0 14px" }}
                    onClick={() => {
                      loadStats();
                      loadChains();
                    }}
                  >
                    <i className="fa fa-filter me-1"></i> Filter
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ height: "36px", padding: "0 12px" }}
                    onClick={() => {
                      try {
                        sessionStorage.removeItem(FILTER_STORAGE_KEY);
                      } catch (_) {}
                      setSelectedProject("");
                      setSelectedWorkOrder("");
                      setStatusFilter("ALL");
                      setSearch("");
                      setPage(1);
                      setLimit(10);
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========================================================
          2.5 PIPELINE LIFECYCLE STAGE GUIDE RIBBON
      ======================================================== */}
      <Row className="mb-3">
        <Col lg={12}>
          <Card className="shadow-sm border-0 mb-0">
            <Card.Body className="py-2 px-3 bg-white rounded">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="d-flex align-items-center justify-content-center rounded"
                    style={{ width: "26px", height: "26px", background: "rgba(100, 24, 195, 0.1)", color: "#6418c3" }}
                  >
                    <i className="fa fa-layer-group fs-12"></i>
                  </span>
                  <div>
                    <span className="fs-12 font-w700 text-dark text-uppercase">
                      Lifecycle Progress Stages
                    </span>
                    <span className="text-muted fs-11 ms-1">(Step-by-Step Progress %)</span>
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                  {[
                    { step: "1", name: "Indent Pending", pct: "25%", color: "#d97706", bg: "rgba(245, 158, 11, 0.08)", border: "rgba(245, 158, 11, 0.25)" },
                    { step: "2", name: "Indent Approved", pct: "35%", color: "#7c3aed", bg: "rgba(124, 58, 237, 0.08)", border: "rgba(124, 58, 237, 0.25)" },
                    { step: "3", name: "PO Issued", pct: "50%", color: "#2563eb", bg: "rgba(37, 99, 235, 0.08)", border: "rgba(37, 99, 235, 0.25)" },
                    { step: "4", name: "Delivered", pct: "70%", color: "#ea580c", bg: "rgba(234, 88, 12, 0.08)", border: "rgba(234, 88, 12, 0.25)" },
                    { step: "5", name: "In DPR", pct: "85%", color: "#0284c7", bg: "rgba(2, 132, 199, 0.08)", border: "rgba(2, 132, 199, 0.25)" },
                    { step: "6", name: "Completed", pct: "100%", color: "#16a34a", bg: "rgba(22, 163, 74, 0.1)", border: "rgba(22, 163, 74, 0.25)" },
                  ].map((item, idx, arr) => (
                    <Fragment key={item.step}>
                      <div
                        className="d-flex align-items-center rounded-pill px-2 py-1"
                        style={{
                          background: item.bg,
                          border: `1px solid ${item.border}`,
                          gap: "6px",
                          fontSize: "11px",
                        }}
                      >
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center text-white font-w700"
                          style={{
                            width: "16px",
                            height: "16px",
                            fontSize: "9px",
                            background: item.color,
                          }}
                        >
                          {item.step}
                        </span>
                        <span className="font-w600" style={{ color: item.color }}>
                          {item.name}
                        </span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: item.color,
                            color: "#ffffff",
                            fontSize: "9px",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            padding: "2px 6px",
                            borderRadius: "50rem",
                          }}
                        >
                          {item.pct}
                        </span>
                      </div>
                      {idx < arr.length - 1 && (
                        <i className="fa fa-chevron-right text-muted fs-10" style={{ opacity: 0.4 }}></i>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========================================================
          3. MAIN TABLE CARD (Native Canscorp Table Layout)
      ======================================================== */}
      <Row>
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="py-3 px-3 border-bottom d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h5 className="card-title font-w700 mb-0">Sequential Tracking Matrix</h5>
                <small className="text-muted">Tracking Indent ➔ Purchase Order ➔ Site Delivery ➔ DPR Execution</small>
              </div>

              <div className="d-flex align-items-center gap-2 mt-sm-0 mt-2">
                <div className="d-flex align-items-center gap-1 text-muted fs-12">
                  <span>Show</span>
                  <Form.Select
                    size="sm"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{ width: "65px", height: "30px" }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : chains.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <h5>No Records Found</h5>
                  <p className="small">Try adjusting project, work order or search filters.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="table-responsive-md mb-0 align-middle">
                    <thead>
                      <tr className="border-bottom text-uppercase fs-12 font-w600 text-muted">
                        <th>#</th>
                        <th>1. Indent Details</th>
                        <th>2. Purchase Order</th>
                        <th>3. Delivery & MIR</th>
                        <th>4. DPR Site Execution</th>
                        <th>
                          Pipeline Status{" "}
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="pipeline-status-tooltip">
                                <strong>Lifecycle Step %:</strong> Requisition lifecycle progress (25% ➔ 100%)<br />
                                <strong>Installed %:</strong> Physical material installed in DPR vs requested
                              </Tooltip>
                            }
                          >
                            <i className="fa fa-question-circle text-muted ms-1" style={{ cursor: "pointer" }}></i>
                          </OverlayTrigger>
                        </th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chains.map((chain, index) => {
                        const rowNum = (page - 1) * limit + index + 1;
                        const completionRate = chain.metrics?.completionPercent || 0;

                        return (
                          <tr key={chain._id} className="border-bottom">
                            {/* # */}
                            <td className="font-w600 fs-12 text-muted">{rowNum}</td>

                            {/* 1. Indent Column */}
                            <td>
                              <div>
                                <strong className="text-primary font-w600 d-block">{chain.indent?.indentNumber}</strong>
                                <span
                                  className="fs-12 text-dark font-w500 d-block text-truncate"
                                  style={{ maxWidth: "240px" }}
                                  title={`${chain.project?.projectName || "—"} ${chain.workOrder?.label ? `(${chain.workOrder.label} - ${chain.workOrder?.woNo || ""})` : chain.workOrder?.woNo ? `(${chain.workOrder.woNo})` : ""}`}
                                >
                                  {chain.project?.projectName || "—"}
                                  {(chain.workOrder?.woNo || chain.workOrder?.label) && (
                                    <span className="text-muted ms-1 small">
                                      ({chain.workOrder?.label ? `${chain.workOrder.label}${chain.workOrder.woNo ? ` - ${chain.workOrder.woNo}` : ""}` : chain.workOrder?.woNo})
                                    </span>
                                  )}
                                </span>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: 1.2,
                                      whiteSpace: "nowrap",
                                      fontWeight: 700,
                                      borderRadius: "50rem",
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      ...(chain.indent?.status === "Approved"
                                        ? { backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" }
                                        : { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }),
                                    }}
                                  >
                                    {chain.indent?.status || "Pending"}
                                  </span>
                                  <span className="fs-11 text-muted">
                                    {chain.indent?.itemsCount || 0} items ({chain.metrics?.totalRequestedQty || 0} Qty)
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* 2. PO Column */}
                            <td>
                              {chain.pos && chain.pos.length > 0 ? (
                                chain.pos.map((po) => (
                                  <div key={po._id} className="mb-1">
                                    <strong className="text-dark font-w600 d-block">{po.poNumber}</strong>
                                    <span className="fs-12 text-muted d-block text-truncate" style={{ maxWidth: "180px" }}>
                                      {po.vendorName}
                                    </span>
                                    <span className="fs-12 font-w700" style={{ color: "#15803d" }}>
                                      ₹{(po.totalAmount || 0).toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: 1.2,
                                      whiteSpace: "nowrap",
                                      fontWeight: 600,
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      backgroundColor: "#f1f5f9",
                                      color: "#64748b",
                                      border: "1px solid #cbd5e1",
                                    }}
                                  >
                                    No PO Issued
                                  </span>
                                  {chain.warnings && chain.warnings.length > 0 && (
                                    <span className="text-danger d-block fs-11 mt-1">
                                      <i className="fa fa-exclamation-circle me-1"></i>
                                      {chain.warnings[0]}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>

                            {/* 3. Delivery Column */}
                            <td>
                              {chain.deliveries && chain.deliveries.length > 0 ? (
                                chain.deliveries.map((del) => (
                                  <div key={del._id} className="mb-1">
                                    <strong className="text-dark font-w600 d-block">{del.deliveryNumber}</strong>
                                    <span className="fs-12 text-muted d-block">
                                      Challan: {del.challanNumber || "—"}
                                    </span>
                                    <span className="fs-11 text-muted">{formatDate(del.createdAt)}</span>
                                  </div>
                                ))
                              ) : (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    backgroundColor: "#fff7ed",
                                    color: "#c2410c",
                                    border: "1px solid #fed7aa",
                                  }}
                                >
                                  Pending Delivery
                                </span>
                              )}
                            </td>

                            {/* 4. DPR Execution Column */}
                            <td>
                              {chain.dprExecution?.totalDPRsCount > 0 ? (
                                <div>
                                  <strong className="font-w600 d-block" style={{ color: "#15803d" }}>
                                    {chain.dprExecution.totalExecutedQty} Installed
                                  </strong>
                                  <span className="fs-12 text-muted d-block">
                                    In {chain.dprExecution.totalDPRsCount} Daily Reports
                                  </span>
                                  {chain.dprExecution.latestDprDate && (
                                    <span className="fs-11 text-muted">
                                      Last: {formatDate(chain.dprExecution.latestDprDate)}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    backgroundColor: "#f8fafc",
                                    color: "#64748b",
                                    border: "1px solid #cbd5e1",
                                  }}
                                >
                                  Pending Installation
                                </span>
                              )}
                            </td>

                            {/* 5. Status & Progress */}
                            <td>
                              <div style={{ minWidth: "130px" }}>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  {getStageBadge(chain.currentStage)}
                                  <span className="fs-12 font-w600">{chain.stageProgress}%</span>
                                </div>
                                <ProgressBar
                                  now={chain.stageProgress}
                                  variant={chain.stageProgress === 100 ? "success" : "primary"}
                                  style={{ height: "5px" }}
                                />
                                <span className="fs-11 text-muted mt-1 d-block">
                                  Installed: {completionRate}% ({chain.metrics?.totalExecutedQty || 0}/{chain.metrics?.totalRequestedQty || 0})
                                </span>
                              </div>
                            </td>

                            {/* 6. Action Button */}
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                title="View Details"
                                className="d-inline-flex align-items-center justify-content-center p-0"
                                style={{ width: "30px", height: "30px", minWidth: "30px", borderRadius: "6px" }}
                                onClick={() => handleOpenDetail(chain)}
                              >
                                <i className="fas fa-eye" style={{ fontSize: "13px" }} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>

            {/* Standard AppPagination Component */}
            <AppPagination
              currentPage={page}
              totalPages={pagination.totalPages}
              totalCount={pagination.total}
              itemsLength={chains.length}
              limit={limit}
              onPageChange={(newPage) => setPage(newPage)}
            />
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ProcurementExecutionDashboard;
